import React, { useState, useEffect } from "react";
import ExportSidebar from "../../../../components/SiteMap/components/ExportSidebar";
import { useData } from "../../../../components/SiteMap/contexts/DataContext";
import { PortalButtonHeader } from "./Default";
import { useMapEditing } from "../../../../components/SiteMap/utils/routing";
import StandardButton from "../../../../components/Buttons/StandardButton";
import exportSiteMap, {
  simplifyExportError,
} from "../../../../components/SiteMap/utils/export";
import {
  checkSiteMapFraming,
  extractSiteMapPoints,
} from "../../../../components/SiteMap/utils/framingCheck";
import { confirmWrapper } from "../../../../components/Modals/ConfirmWrapper";
import { useParams } from "react-router-dom";
import { useMap } from "@vis.gl/react-google-maps";
import { formatAddressOneLine } from "../../../../services/axios/jobs";
import flash from "../../../../services/flash";

function safeFileName(input) {
  return input.replaceAll(/\s+/g, "_").replaceAll(/[^0-9a-zA-Z-_]/g, "");
}

const SiteMapExportPage = () => {
  const {
    surfacesVisibility,
    setSurfacesVisibility,
    setAllSurfacesVisibility,
    disableUi,
    propertyAddress,
    propertyName,
    surfaceFeatures,
    boundary,
    hazardMarkers,
    labelMarkers,
    arrows,
  } = useData();
  const map = useMap();

  const { jobId, season } = useParams();

  const { setEditing } = useMapEditing();
  let defaultFileName = "Site_Map";
  if (propertyName) {
    defaultFileName += "_" + safeFileName(propertyName);
  }
  if (propertyAddress) {
    defaultFileName +=
      "_" + safeFileName(formatAddressOneLine(propertyAddress));
  }
  const [fileName, setFileName] = useState(defaultFileName);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Cleanup hook to reset features visibility when component unmounts
  // setAllSurfacesVisibility *must* be setup with a useCallback for this to work
  useEffect(() => {
    return () => {
      // reset surfacesVisibility to everything visible
      // TODO: this is an issue for the Render endpoint since it this component gets rendered more than once overriding the visibility set by the Render endpoint.
      // Need to find a different place to set everything visible.
      // setAllSurfacesVisibility(true);
    };
  }, [setAllSurfacesVisibility]);

  const onCancel = () => {
    if (isExporting) return;
    setEditing(false);
  };
  const onSave = async () => {
    if (isExporting) return;
    setExportError(null);

    // Pre-export framing check: warn if features are cut off or excessively zoomed out
    if (map) {
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (center && typeof zoom === "number") {
        const points = extractSiteMapPoints({
          surfaceFeatures,
          boundary,
          hazardMarkers,
          labelMarkers,
          arrows,
        });

        const framingResult = checkSiteMapFraming({
          center: { lat: center.lat(), lng: center.lng() },
          zoom,
          points,
        });

        if (!framingResult.isValid) {
          const shouldProceed = await confirmWrapper(framingResult.title, {
            description: `${framingResult.warningMessage}\n\nDo you want to adjust the map, or export anyway?`,
            okText: "Export Anyway",
            cancelText: "Adjust Map",
          });
          if (!shouldProceed) {
            return;
          }
        }
      }
    }

    try {
      setIsExporting(true);
      await exportSiteMap({
        jobId,
        state: {
          surfacesVisibility,
          zoom: map?.getZoom?.(),
          center: map?.getCenter?.(),
        },
        season: season,
        fileName,
      });
      flash.success("Site map exported successfully!");
      setEditing(false);
    } catch (err) {
      const friendlyMessage = simplifyExportError(err);
      setExportError(friendlyMessage);
      flash.error(friendlyMessage);
    } finally {
      setIsExporting(false);
    }
  };
  return (
    <>
      <PortalButtonHeader>
        <div className="flex flex-col items-end justify-center">
          <div className="flex items-center">
            <button
              onClick={onCancel}
              disabled={isExporting}
              className={`px-6 py-2.5 rounded-xl font-bold text-sm bg-snow-gray mr-4 ${
                isExporting
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:opacity-80 active:opacity-40"
              }`}
            >
              Cancel
            </button>

            <input
              type="text"
              value={fileName}
              disabled={isExporting}
              onChange={(e) => {
                setFileName(e.target.value);
                if (exportError) setExportError(null);
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 mr-4 text-sm w-48 disabled:opacity-50"
            />

            <StandardButton
              color="green"
              label={isExporting ? "Exporting..." : "Export"}
              loading={isExporting}
              disabled={isExporting}
              className="inline-block"
              onClick={onSave}
            />
          </div>

          {exportError && !isExporting && (
            <span className="text-xs text-red-600 font-medium mt-1 max-w-sm text-right">
              {exportError}
            </span>
          )}
        </div>
      </PortalButtonHeader>
      {disableUi === true ? null : (
        <>
          <ExportSidebar
            surfacesVisibility={surfacesVisibility}
            setSurfacesVisibility={setSurfacesVisibility}
          />
          {/* The following div is a frame that shows the user what will be included in the screenshot */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: "720px",
              height: "720px",
              transform: "translate(-50%, -50%)",
              outline: "20px solid red",
              pointerEvents: "none",
              color: "red",
              fontSize: "18px",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                opacity: 0.95,
                padding: "10px 18px",
              }}
            >
              {exportError
                ? exportError
                : "Drag the map and zoom to fit everything in the red frame."}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SiteMapExportPage;
